import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { usePonds, useMasterData, useMutation } from '../../hooks/useApi';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

interface FishSeedFormData {
  pond_id: string;
  stocking_date: string;
  fish_species: string[];  // Multi-species only
  species_breakdown: {[key: string]: {weight_kg: number, count: number}};  // Species-specific breakdown
  total_weight_kg: number;
  fish_count: number;
  source_supplier?: string;
  remarks?: string;
  location_id?: string;
}

interface FishSeedLog {
  id: string;
  pond_id: string;
  stocking_date: string;
  fish_species: string;
  total_weight_kg?: number;
  fish_count?: number;
  avg_weight_g?: number;
  source_supplier?: string;
  remarks?: string;
  created_at: string;
  created_by: string;
}

interface PondSeedData {
  id: string;
  name: string;
  pond_type: string;
  source_supplier: string;
  remarks: string;
  expanded: boolean;
  selected_species: string[];  // Multi-species selection (mandatory)
  species_breakdown: {[key: string]: {weight_kg: string, count: string}};  // Species-specific breakdown
}

const FishSeedForm: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [stockingDate, setStockingDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [pondsData, setPondsData] = useState<PondSeedData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const { data: fishSpecies } = useMasterData('fish_species', selectedLocation?.id || null);
  const { mutate: createFishSeedLog, loading: creating } = useMutation<FishSeedLog, FishSeedFormData>();

  useEffect(() => {
    if (ponds) {
      setPondsData(ponds.map(pond => ({
        id: pond.id,
        name: pond.name,
        pond_type: pond.pond_type,
        source_supplier: '',
        remarks: '',
        expanded: false,
        selected_species: [],
        species_breakdown: {}
      })));
    }
  }, [ponds]);

  const updatePond = (pondId: string, field: keyof PondSeedData, value: string | boolean | string[] | {[key: string]: {weight_kg: string, count: string}}) => {
    setPondsData(pondsData.map(pond => 
      pond.id === pondId ? { ...pond, [field]: value } : pond
    ));
  };

  const handleSpeciesSelection = (pondId: string, species: string, selected: boolean) => {
    setPondsData(pondsData.map(pond => {
      if (pond.id === pondId) {
        let newSelectedSpecies = [...pond.selected_species];
        if (selected) {
          if (!newSelectedSpecies.includes(species)) {
            newSelectedSpecies.push(species);
          }
        } else {
          newSelectedSpecies = newSelectedSpecies.filter(s => s !== species);
        }
        
        // Initialize breakdown for new species
        const newBreakdown = { ...pond.species_breakdown };
        if (selected && !newBreakdown[species]) {
          newBreakdown[species] = { weight_kg: '', count: '' };
        } else if (!selected) {
          delete newBreakdown[species];
        }
        
        return { ...pond, selected_species: newSelectedSpecies, species_breakdown: newBreakdown };
      }
      return pond;
    }));
  };

  const updateSpeciesBreakdown = (pondId: string, species: string, field: 'weight_kg' | 'count', value: string) => {
    setPondsData(pondsData.map(pond => {
      if (pond.id === pondId) {
        const newBreakdown = { ...pond.species_breakdown };
        if (!newBreakdown[species]) {
          newBreakdown[species] = { weight_kg: '', count: '' };
        }
        newBreakdown[species][field] = value;
        return { ...pond, species_breakdown: newBreakdown };
      }
      return pond;
    }));
  };

  const togglePondExpansion = (pondId: string) => {
    setPondsData(pondsData.map(pond => 
      pond.id === pondId ? { ...pond, expanded: !pond.expanded } : pond
    ));
  };

  const handleVoiceInput = (pondId: string) => {
    alert(`Voice input for ${pondId} - Feature would integrate with device microphone`);
  };

  // Bulk actions for efficiency
  // const copyToAllPonds = (field: keyof PondSeedData, value: string) => {
  //   setPondsData(pondsData.map(pond => ({ ...pond, [field]: value })));
  // };

  const clearAllPonds = () => {
    setPondsData(pondsData.map(pond => ({ 
      ...pond, 
      source_supplier: '',
      remarks: '',
      selected_species: [],
      species_breakdown: {}
    })));
  };

  // const calculateAvgWeight = (weight: string, count: string) => {
  //   const w = parseFloat(weight);
  //   const c = parseInt(count);
  //   if (w && c && c > 0) {
  //     return ((w * 1000) / c).toFixed(1);
  //   }
  //   return null;
  // };

  const handleSubmit = async () => {
    const validEntries = pondsData.filter(pond => {
      // Species selection is mandatory
      if (pond.selected_species.length === 0) {
        return false;
      }
      // All selected species must have complete breakdown data
      return pond.selected_species.every(species => {
        const breakdown = pond.species_breakdown[species];
        return breakdown && breakdown.weight_kg && breakdown.count;
      });
    });
    
    if (validEntries.length === 0) {
      alert('Please select species and fill stocking data for at least one pond');
      return;
    }

    try {
      setLoading(true);
      const promises = validEntries.map(pond => {
        // Calculate total weight and count from species breakdown
        let totalWeight = 0;
        let totalCount = 0;
        const speciesBreakdown: {[key: string]: {weight_kg: number, count: number}} = {};
        
        pond.selected_species.forEach(species => {
          const breakdown = pond.species_breakdown[species];
          if (breakdown && breakdown.weight_kg && breakdown.count) {
            const weight = parseFloat(breakdown.weight_kg);
            const count = parseInt(breakdown.count);
            totalWeight += weight;
            totalCount += count;
            speciesBreakdown[species] = { weight_kg: weight, count: count };
          }
        });

        const formData: FishSeedFormData = {
          pond_id: pond.id,
          stocking_date: stockingDate,
          location_id: selectedLocation?.id,
          fish_species: pond.selected_species,
          species_breakdown: speciesBreakdown,
          total_weight_kg: totalWeight,
          fish_count: totalCount,
          source_supplier: pond.source_supplier || undefined,
          remarks: pond.remarks || undefined,
        };

        return createFishSeedLog('/fish-seed-logs', formData);
      });
      
      await Promise.all(promises);
      alert(`✅ Successfully recorded fish stocking data for ${validEntries.length} ponds on ${stockingDate}`);
      
      // Reset form
      setPondsData(pondsData.map(pond => ({ 
        ...pond, 
        source_supplier: '',
        remarks: '',
        selected_species: [],
        species_breakdown: {}
      })));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTotalWeight = () => {
    return pondsData.reduce((sum, pond) => {
      let pondWeight = 0;
      pond.selected_species.forEach(species => {
        const breakdown = pond.species_breakdown[species];
        if (breakdown && breakdown.weight_kg) {
          pondWeight += parseFloat(breakdown.weight_kg) || 0;
        }
      });
      return sum + pondWeight;
    }, 0).toFixed(1);
  };

  const getTotalCount = () => {
    return pondsData.reduce((sum, pond) => {
      let pondCount = 0;
      pond.selected_species.forEach(species => {
        const breakdown = pond.species_breakdown[species];
        if (breakdown && breakdown.count) {
          pondCount += parseInt(breakdown.count) || 0;
        }
      });
      return sum + pondCount;
    }, 0);
  };

  const getFilledPondsCount = () => {
    return pondsData.filter(pond => {
      if (pond.selected_species.length === 0) return false;
      return pond.selected_species.every(species => {
        const breakdown = pond.species_breakdown[species];
        return breakdown && breakdown.weight_kg && breakdown.count;
      });
    }).length;
  };

  if (!selectedLocation) {
    return (
      <div className="max-w-md mx-auto bg-gray-50 min-h-screen py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/data-entry" className="p-1 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-lg font-bold text-gray-800">🐠 Fish Seed Stocking - EOD</h1>
        </div>
        
        {/* Date Selection */}
        <div className="flex items-center gap-2 mb-3">
          <input
            type="date"
            value={stockingDate}
            onChange={(e) => setStockingDate(e.target.value)}
            className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={clearAllPonds}
            className="bg-red-100 text-red-700 py-2 px-3 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors"
          >
            <span className="text-xs">🔄</span>
            Clear All
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="bg-cyan-50 p-2 rounded-lg">
          <div className="flex justify-between text-xs text-cyan-700 mb-1">
            <span>Progress</span>
            <span>{getFilledPondsCount()}/{pondsData.length} ponds completed</span>
          </div>
          <div className="w-full bg-cyan-200 rounded-full h-2">
            <div 
              className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(getFilledPondsCount() / pondsData.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Ponds List */}
      <div className="p-4 space-y-3">
        {pondsData.map((pond) => {
          return (
            <div key={pond.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Pond Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{pond.name}</h3>
                    <p className="text-sm text-gray-500">{pond.pond_type === 'nursery' ? 'Nursery' : 'Grower'}</p>
                  </div>
                  <button
                    onClick={() => togglePondExpansion(pond.id)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    {pond.expanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Entry Fields */}
              <div className="p-4 space-y-3">
                {/* Species Selection - Mandatory */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Species Selection *
                  </label>
                  <div className="text-xs text-gray-500 mb-2">
                    Select one or more species for stocking (mandatory)
                  </div>
                  
                  {fishSpecies && fishSpecies.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {fishSpecies.map((species: any) => (
                        <label key={species.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={pond.selected_species.includes(species.name)}
                            onChange={(e) => handleSpeciesSelection(pond.id, species.name, e.target.checked)}
                            className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                          />
                          <span className="text-sm text-gray-700 capitalize">
                            {species.name.replace('_', ' ')}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Species Breakdown */}
                  {pond.selected_species.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-gray-700">Species Breakdown</div>
                      {pond.selected_species.map(species => {
                        const breakdown = pond.species_breakdown[species] || { weight_kg: '', count: '' };
                        const speciesAvgWeight = breakdown.weight_kg && breakdown.count ? 
                          ((parseFloat(breakdown.weight_kg) * 1000) / parseInt(breakdown.count)).toFixed(1) : null;
                        
                        return (
                          <div key={species} className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-sm font-medium text-gray-700 mb-2 capitalize">
                              {species.replace('_', ' ')}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Weight (kg)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={breakdown.weight_kg}
                                  onChange={(e) => updateSpeciesBreakdown(pond.id, species, 'weight_kg', e.target.value)}
                                  placeholder="0.00"
                                  className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Count</label>
                                <input
                                  type="number"
                                  value={breakdown.count}
                                  onChange={(e) => updateSpeciesBreakdown(pond.id, species, 'count', e.target.value)}
                                  placeholder="0"
                                  className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                />
                              </div>
                            </div>
                            {speciesAvgWeight && (
                              <div className="mt-2 text-xs text-green-600">
                                Avg Weight: {speciesAvgWeight}g
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Show warning if no species selected */}
                  {pond.selected_species.length === 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="text-xs text-yellow-700 font-medium">⚠️ Species Selection Required</div>
                      <div className="text-xs text-yellow-600">Please select at least one species to continue</div>
                    </div>
                  )}
                </div>

                {/* Expanded Fields */}
                {pond.expanded && (
                  <div className="space-y-3 border-t border-gray-100 pt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Source/Supplier
                      </label>
                      <input
                        type="text"
                        value={pond.source_supplier}
                        onChange={(e) => updatePond(pond.id, 'source_supplier', e.target.value)}
                        placeholder="Supplier name or source"
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-700">
                          Remarks (Optional)
                        </label>
                        <button
                          onClick={() => handleVoiceInput(pond.id)}
                          className="p-1 text-gray-400 hover:text-cyan-600 transition-colors"
                        >
                          <span className="text-sm">🎤</span>
                        </button>
                      </div>
                      <textarea
                        value={pond.remarks}
                        onChange={(e) => updatePond(pond.id, 'remarks', e.target.value)}
                        placeholder="Transportation, condition, mortality during stocking, etc."
                        rows={2}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary & Submit */}
      <div className="p-4 bg-white border-t border-gray-200 sticky bottom-0">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-cyan-50 p-3 rounded-lg text-center">
            <div className="text-xs text-cyan-600 font-medium">Total Weight</div>
            <div className="text-lg font-bold text-cyan-700">{getTotalWeight()} kg</div>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg text-center">
            <div className="text-xs text-blue-600 font-medium">Total Fish Count</div>
            <div className="text-lg font-bold text-blue-700">{getTotalCount()}</div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading || creating}
          className="w-full bg-cyan-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-cyan-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading || creating ? <LoadingSpinner size="sm" /> : <span className="text-lg">💾</span>}
          Submit EOD Stocking ({getFilledPondsCount()}/{pondsData.length})
        </button>
      </div>
    </div>
  );
};

export default FishSeedForm;